import * as v2 from '@/core/services/api/v2';

const state = {
  missions: [],
  reward: null,
  progress: {
    completedMissions: 0,
    totalMissions: 0,
    progress: 0
  },
  isLoading: false,
  error: null,
  lastCompletedMission: null,
  previousCompletedCount: 0 // Add this to track previous count
};

const mutations = {
  SET_MISSIONS(state, missions) {
    state.missions = missions;
  },
  SET_REWARD(state, reward) {
    state.reward = reward;
  },
  SET_PROGRESS(state, progress) {
    // Store previous count before updating
    state.previousCompletedCount = state.progress.completedMissions;
    state.progress = progress;
  },
  SET_LOADING(state, isLoading) {
    state.isLoading = isLoading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  SET_LAST_COMPLETED_MISSION(state, mission) {
    state.lastCompletedMission = mission;
  }
};

const actions = {
  /**
   * Busca as missões do dia no V2. O GET **atribui** as três missões na
   * primeira chamada do dia — não existe mais a rota `initialize` do Strapi.
   * Cada missão já vem com title/type/amountRequired e o progresso derivado
   * dos contadores do servidor, então uma chamada alimenta as duas fatias de
   * estado (`missions` e `progress`) que antes vinham de rotas separadas.
   */
  async fetchMissions({ commit, rootState }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      if (!playerId) return;

      // O prêmio vem de uma rota irmã (11/09/2026). Se ela falhar, as missões
      // aparecem do mesmo jeito — só o prêmio some, e `rewardFormatted` já
      // devolve null sem quebrar.
      const [missions, reward] = await Promise.all([
        v2.missoes(playerId),
        v2.premioDasMissoes(playerId).catch(() => null),
      ]);

      commit('SET_MISSIONS', missions);
      commit('SET_REWARD', reward);

      const completedMissions = missions.filter((m) => m.isComplete).length;
      const totalMissions = missions.length;
      commit('SET_PROGRESS', {
        completedMissions,
        totalMissions,
        // Média inteira dos progressos individuais — mesma semântica do campo
        // `progress` agregado que o Strapi calculava no servidor.
        progress:
          totalMissions > 0
            ? Math.round(missions.reduce((soma, m) => soma + (m.progress || 0), 0) / totalMissions)
            : 0,
        // Cada missão já traz id/currentAmount/isComplete/progress, que é o
        // que o getter `missionsWithProgress` cruza com `state.missions`.
        missions
      });
    } catch (error) {
      console.error('Error fetching daily missions:', error);
      commit(
        'SET_ERROR',
        error.response?.data?.message || error.response?.data?.error?.message || error.message
      );
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * O V2 não tem rota de update-progress: o progresso é derivado de
   * contadores do servidor (peixes pescados, vendas...), atualizados pelas
   * próprias ações do jogo. Esta action virou só "rebusca e detecta": compara
   * o total de completas antes e depois do fetch e mantém o mesmo
   * `eventDetail` e o mesmo evento `mission-completed` que a UI já escuta.
   */
  async updateMissionProgress({ commit, dispatch, state }) {
    try {
      // Guarda o total antes do fetch — o SET_PROGRESS de dentro do fetch
      // sobrescreve `state.progress`, então a comparação precisa ser feita
      // com o valor capturado aqui.
      const previousCompletedCount = state.progress.completedMissions;

      await dispatch('fetchMissions');

      const completedMissions = state.progress.completedMissions;
      const totalMissions = state.progress.totalMissions;
      const detectedCompletion = completedMissions > previousCompletedCount;

      // "Novas completas" que ainda têm resgate pendente — o backend não diz
      // quais completaram agora, então a lista são as completas não resgatadas.
      const newlyCompletedMissions = detectedCompletion
        ? state.missions.filter((m) => m.isComplete && !m.claimed)
        : [];

      const eventDetail = {
        missions: newlyCompletedMissions,
        newlyCompletedMissions,
        completedMissions,
        totalMissions,
        allComplete: totalMissions > 0 && completedMissions === totalMissions,
        updated: true,
        progress: state.progress.progress || 0,
        previousCompletedCount,
        detectedCompletion
      };

      if (detectedCompletion) {
        if (newlyCompletedMissions.length > 0) {
          commit('SET_LAST_COMPLETED_MISSION', newlyCompletedMissions[0]);
        }
        window.dispatchEvent(new CustomEvent('mission-completed', {
          detail: eventDetail
        }));
      }

      return eventDetail;
    } catch (error) {
      console.error('Error updating mission progress:', error);
      const message =
        error.response?.data?.message || error.response?.data?.error?.message || error.message;
      commit('SET_ERROR', message);
      return { success: false, error: message };
    }
  },

  /**
   * Resgata a recompensa do dia. Tudo-ou-nada, decidido no servidor: 400 se
   * há missão incompleta, 400 se já resgatada hoje.
   */
  async claimReward({ commit, dispatch, rootState }) {
    commit('SET_LOADING', true);
    try {
      const playerId = rootState.player?.id;
      if (!playerId) {
        console.warn('Cannot claim reward: No player ID available');
        return { success: false, error: 'No player ID' };
      }

      const { type, amount } = await v2.resgatarMissoes(playerId);

      // Atualiza o saldo credenciado no servidor: gold tem rota leve própria;
      // parallelium só aparece recarregando o estado do jogador.
      if (type === 'gold') {
        await dispatch('player/updateGold', null, { root: true });
      } else {
        await dispatch('player/loadPlayerState', playerId, { root: true });
      }

      // Set claimed status in player state
      commit('player/SET_CLAIMED_DAILY_MISSION_REWARD', true, { root: true });

      // Refresh missions after claiming
      await dispatch('fetchMissions');

      // `parallelium` vem no singular do catálogo; o cliente sempre falou no
      // plural (`paralleliums`) — mesma tradução que o getter fazia.
      return {
        success: true,
        reward: { type: type === 'parallelium' ? 'paralleliums' : type, amount }
      };
    } catch (error) {
      console.error('Error claiming reward:', error);
      const message =
        error.response?.data?.message || error.response?.data?.error?.message || error.message;
      commit('SET_ERROR', message);
      return { success: false, error: message };
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * No V2 não existe rota de initialize: o próprio GET atribui as missões do
   * dia na primeira chamada. Buscar já é inicializar.
   */
  async initializeMissions({ dispatch }) {
    await dispatch('fetchMissions');
  }
};

const getters = {
  missionsWithProgress: (state) => {
    if (!state.missions || !state.progress.missions) return [];

    return state.missions.map(mission => {
      const progressData = state.progress.missions?.find(m => m.id === mission.id) || {
        currentAmount: 0,
        isComplete: false,
        progress: 0
      };

      return {
        ...mission,
        ...progressData
      };
    });
  },

  /**
   * O prêmio de hoje já saiu. Lido das missões, que é a mesma linha que o
   * servidor confere antes de recusar o resgate — e não do perfil do jogador,
   * que qualquer `loadPlayerState` sobrescreve.
   */
  rewardClaimed: (state) => state.missions.some((m) => m.claimed),

  allMissionsComplete: (state) => {
    if (!state.progress) return false;
    return state.progress.completedMissions === state.progress.totalMissions &&
           state.progress.totalMissions > 0;
  },

  overallProgress: (state) => {
    if (!state.progress) return 0;
    return state.progress.progress || 0;
  },

  rewardFormatted: (state) => {
    if (!state.reward) return null;

    const rewardType = state.reward.type === 'parallelium' ? 'paralleliums' : 'gold';
    return {
      type: rewardType,
      amount: state.reward.amount,
      tier: state.reward.tier
    };
  },

  getLastCompletedMission: state => state.lastCompletedMission
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
};
